import React, { useState, useEffect } from 'react';
import { BrainCircuit, Loader, AlertTriangle, Book, Clock } from 'lucide-react';
import { useNavigate } from 'react-router-dom';
import { BibleAI } from '../lib/ai';
import { bibleService } from '../services/bibleService';
import { PreviousQuestionsDropdown } from './PreviousQuestionsDropdown';
import { AIResponse, ConversationEntry, VerseReference } from '../types/ai';

export function BibleGeneralAIAssistant() {
  const navigate = useNavigate();
  const [question, setQuestion] = useState('');
  const [answer, setAnswer] = useState<AIResponse | null>(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [ai, setAI] = useState<BibleAI | null>(null);
  const [history, setHistory] = useState<ConversationEntry[]>([]);
  const [showHistory, setShowHistory] = useState(false);

  useEffect(() => {
    // Load conversation history from localStorage
    const savedHistory = localStorage.getItem('bibleAIHistory');
    if (savedHistory) {
      try {
        setHistory(JSON.parse(savedHistory));
      } catch (err) {
        setError('Failed to load conversation history');
      }
    }

    const initAI = async () => {
      try {
        const instance = await BibleAI.getInstance();
        setAI(instance);
        setError(null);
      } catch (err) {
        setError('Unable to initialize AI features. Please try again later.');
      }
    };

    initAI();
  }, []);

  const extractVerseReferences = (text: string): string[] => {
    // Basic regex for common Bible verse formats
    const verseRegex = /(?:\d\s)?[A-Za-z]+\s\d+:\d+(?:-\d+)?/g;
    return text.match(verseRegex) || [];
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!ai || !question.trim()) return;

    setLoading(true);
    setError(null);
    setAnswer(null);
    
    try {
      const result = await ai.answerQuestion(question, '');
      const references = extractVerseReferences(result.answer);
      const verseRefs: VerseReference[] = [];

      for (const ref of references) {
        try {
          const verseText = await bibleService.getPassage('kjv', ref);
          if (verseText) {
            verseRefs.push({
              reference: ref,
              text: verseText.content
            });
          }
        } catch (err) {
          // Skip failed verse lookups silently
          continue;
        }
      }

      const newResponse = {
        answer: result.answer,
        references: verseRefs,
        error: result.error
      };

      setAnswer(newResponse);

      // Add to history
      const newEntry: ConversationEntry = {
        question: question.trim(),
        response: newResponse,
        timestamp: Date.now()
      };

      const updatedHistory = [newEntry, ...history].slice(0, 10); // Keep last 10 conversations
      setHistory(updatedHistory);
      localStorage.setItem('bibleAIHistory', JSON.stringify(updatedHistory));

    } catch (err) {
      setError('Failed to get an answer. Please try again.');
    } finally {
      setLoading(false);
    }
  };

  const navigateToVerse = (reference: string) => {
    // Parse the reference (e.g., "John 3:16" -> { book: "John", chapter: 3, verse: 16 })
    const match = reference.match(/^(\d\s)?([A-Za-z]+)\s(\d+):(\d+)(?:-\d+)?$/);
    if (match) {
      const book = match[1] ? match[1].trim() + ' ' + match[2] : match[2];
      const chapter = parseInt(match[3]);
      const verse = parseInt(match[4]);
      
      // Format book name for URL (e.g., "1 John" -> "1-john")
      const bookForUrl = book.toLowerCase().replace(/ /g, '-');
      
      // Navigate to the Bible reader
      navigate(`/read/kjv/${bookForUrl}/${chapter}`);
    }
  };

  const renderAnswer = (response: AIResponse) => (
    <div 
      className="mt-6 p-4 bg-white/5 rounded-lg border border-white/10"
      data-tutorial="ai-response"
    >
      <div className="space-y-6">
        <div className="p-4 bg-white/5 rounded-lg">
          <h3 className="font-medium text-gold-400 mb-3 text-lg">Answer:</h3>
          <p className="text-white/90 leading-relaxed whitespace-pre-wrap">
            {response.answer.split(/(\b(?:\d\s)?[A-Za-z]+\s\d+:\d+(?:-\d+)?\b)/).map((part, index) => {
              const isVerseReference = /^(?:\d\s)?[A-Za-z]+\s\d+:\d+(?:-\d+)?$/.test(part);
              return isVerseReference ? (
                <button
                  key={index}
                  onClick={() => navigateToVerse(part)}
                  className="text-gold-400 hover:text-gold-300 font-medium mx-1 transition-colors"
                >
                  {part}
                </button>
              ) : (
                <span key={index}>{part}</span>
              );
            })}
          </p>
        </div>

        {response.references && response.references.length > 0 && (
          <div className="p-4 bg-white/5 rounded-lg">
            <h3 className="font-medium text-gold-400 mb-3 text-lg flex items-center gap-2">
              <Book size={20} />
              Referenced Verses:
            </h3>
            <div className="space-y-3">
              {response.references.map((ref, index) => (
                <div key={index} className="p-3 rounded bg-white/5">
                  <button
                    onClick={() => navigateToVerse(ref.reference)}
                    className="text-gold-400 hover:text-gold-300 font-medium mb-2 transition-colors"
                  >
                    {ref.reference}
                  </button>
                  <p className="text-white/80 text-sm">{ref.text}</p>
                </div>
              ))}
            </div>
          </div>
        )}
      </div>
    </div>
  );

  if (error) {
    return (
      <div className="bg-red-500/10 rounded-lg p-4">
        <div className="flex items-center gap-2 text-red-400">
          <AlertTriangle size={20} />
          <p className="font-medium">{error}</p>
        </div>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      <div className="flex items-center gap-2 mb-4">
        <BrainCircuit className="text-gold-400" size={28} />
        <h2 className="text-2xl font-semibold text-white">Bible AI Assistant</h2>
      </div>

      <form onSubmit={handleSubmit} className="space-y-4">
        <div className="space-y-3" data-tutorial="ai-input">
          <label htmlFor="question" className="block text-base font-medium text-white/80">
            Ask a question about the Bible:
          </label>
          <input
            type="text"
            id="question"
            value={question}
            onChange={(e) => setQuestion(e.target.value)}
            placeholder="What does the Bible say about..."
            className="w-full px-4 py-3 bg-white/5 border border-white/10 rounded-lg text-white placeholder:text-white/40 focus:outline-none focus:border-gold-500/50"
            disabled={loading}
          />
          
          {/* Previous Questions Dropdown */}
          <div data-tutorial="ai-history">
            <PreviousQuestionsDropdown
              history={history}
              showHistory={showHistory}
              setShowHistory={setShowHistory}
              onSelectQuestion={setQuestion}
            />
          </div>
        </div>

        <div>
          <button
            type="submit"
            disabled={loading || !question.trim()}
            className={`px-6 py-3 rounded-lg font-medium flex items-center gap-2
              ${loading || !question.trim()
                ? 'bg-white/5 text-white/40 cursor-not-allowed'
                : 'bg-gold-500 text-white hover:bg-gold-400 transition-colors'
              }`}
          >
            {loading ? (
              <>
                <Loader className="animate-spin" size={18} />
                Thinking...
              </>
            ) : (
              'Ask Question'
            )}
          </button>
        </div>
      </form>

      {/* Display current answer */}
      {answer && (
        <div className="mt-8" data-tutorial="ai-response">
          {renderAnswer(answer)}
        </div>
      )}

      {/* Display last question and answer */}
      {history.length > 0 && !answer && (
        <div className="mt-8 p-4 bg-white/5 rounded-lg border border-white/10" data-tutorial="ai-response">
          <div className="flex items-center gap-2 mb-4">
            <Clock className="text-gold-400" size={20} />
            <h3 className="text-lg font-medium text-white">Last Question</h3>
          </div>
          <div className="mb-4">
            <p className="text-white/60 mb-2">Question:</p>
            <p className="text-white/90">{history[0].question}</p>
          </div>
          {renderAnswer(history[0].response)}
        </div>
      )}
    </div>
  );
}
